import React, { Component, ReactNode } from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Button } from './yoda/components/Button/Button';
import {
  ErrorContainer,
  ErrorTitle,
  ErrorMessage,
  ErrorButton,
} from './StoreHydrationBoundary.styles.tsx';

interface Props extends WithTranslation {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  isHydrationError: boolean;
}

class StoreHydrationBoundaryBase extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, isHydrationError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    const isHydrationError =
      error.message.includes('hydration') ||
      error.message.includes('Hydration');
    return { hasError: true, error, isHydrationError };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log other errors normally
    console.error('Store error:', error, errorInfo);
  }

  handleReload = () => {
    // Clear localStorage and reload to fix hydration issues
    if (typeof window !== 'undefined') {
      // Clear all botunify-related localStorage keys
      localStorage.removeItem('bot-unify-bots');
      localStorage.removeItem('bot-unify-nav');
      localStorage.removeItem('botunify-query-cache'); // Clear TanStack Query cache
      localStorage.removeItem('auth-store'); // Clear auth store

      // Clear all keys that start with 'botunify' or 'bot-unify'
      Object.keys(localStorage).forEach(key => {
        if (key.startsWith('botunify') || key.startsWith('bot-unify')) {
          localStorage.removeItem(key);
        }
      });

      // Reload the page
      window.location.reload();
    }
  };

  render() {
    const { t } = this.props;

    if (this.state.hasError) {
      if (this.state.isHydrationError) {
        return (
          <ErrorContainer>
            <ErrorTitle>
              {t('common.error.hydrationError', 'Synchronization Error')}
            </ErrorTitle>
            <ErrorMessage>
              {t(
                'common.error.hydrationMessage',
                'Your local preferences are out of sync. Click the button below to reset and reload.'
              )}
            </ErrorMessage>
            <ErrorButton>
              <Button variant='default' onClick={this.handleReload}>
                {t('common.error.resetAndReload', 'Reset and Reload')}
              </Button>
            </ErrorButton>
          </ErrorContainer>
        );
      }

      return (
        this.props.fallback || (
          <ErrorContainer>
            <ErrorTitle>{t('common.error.title')}</ErrorTitle>
            <ErrorMessage>
              {t('common.error.resettingPreferences')}
            </ErrorMessage>
          </ErrorContainer>
        )
      );
    }

    return this.props.children;
  }
}

export const StoreHydrationBoundary = withTranslation()(
  StoreHydrationBoundaryBase
);
